import { useEffect, useId, useRef, type ReactNode } from "react";

interface ModalProps {
  open: boolean;
  title: string;
  description?: string;
  onClose: () => void;
  children?: ReactNode;
  /** Alt aksiyon çubuğu (onay/iptal düğmeleri). */
  footer?: ReactNode;
  size?: "sm" | "md" | "lg";
}

const WIDTH = {
  sm: "max-w-md",
  md: "max-w-xl",
  lg: "max-w-3xl",
} as const;

/**
 * Erişilebilir diyalog.
 * - Esc kapatır, arka plana tıklamak kapatır.
 * - Açılınca odak diyaloğa taşınır, kapanınca tetikleyen öğeye geri döner.
 * - Tab odağı diyalog içinde döner (basit odak tuzağı).
 * Eski panelde diyalog yoktu; `confirm()`/`alert()` kullanılıyordu ve klavye
 * kullanıcısı için hiçbir bağlam bırakmıyordu.
 *
 * ODAK KAYBI HATASI (düzeltildi): odak/Esc etkisi `onClose`'a bağımlıydı.
 * Çağıran sayfalar `onClose={() => setX(false)}` gibi satır içi ok fonksiyonu
 * geçiyor; forma her harf yazıldığında sayfa yeniden render olup YENİ bir
 * `onClose` kimliği üretiyordu. Etki her harfte temizlenip yeniden çalışıyor,
 * temizlik `previouslyFocused.focus()`, yeniden çalışma `panel.focus()` diyerek
 * odağı input'tan çalıyordu. Artık `onClose` bir ref'te tutulur; etki YALNIZCA
 * `open` değişince çalışır.
 *
 * TAŞMA HATASI (düzeltildi): panelde ne `max-height` ne de iç kaydırma kabı
 * vardı. `fixed inset-0` kapsayıcı da kaymıyordu ve açılışta `body` üzerine
 * `overflow: hidden` yazıldığı için sayfa da kayamıyordu. Sonuç: içerik
 * ekrandan uzunsa alt kısmı (tablonun devamı, "Yine de tamamla" bloğu, onay
 * düğmeleri) ERİŞİLEMEZ oluyordu — atölye tabletinde ve telefonda üretim
 * tamamlanamıyordu. Artık panel bir flex sütun: başlık ve alt aksiyon çubuğu
 * sabit, YALNIZCA orta gövde kayar; panel yüksekliği görsel viewport ile
 * sınırlanır (`.modal-panel`, index.css).
 *
 * KLAVYE: telefon/tablette yazılım klavyesi açılınca `visualViewport.height`
 * küçülür ama `100dvh` iOS'ta küçülmez. Bu yüzden gerçek görünür yükseklik
 * `--modal-vh` özel özelliğine yazılır; panel kısalır, odaklanan giriş alanı
 * gövde kaydırıcısı içinde görünür kalır.
 */
export function Modal({
  open,
  title,
  description,
  onClose,
  children,
  footer,
  size = "md",
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const descId = useId();

  // Kapatma geri çağrısı ref'te tutulur — kimliği değişince etki YENİDEN ÇALIŞMAZ.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;
    // Odağı panele taşı — ekran okuyucu başlığı okusun.
    panelRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab") return;

      const panel = panelRef.current;
      if (!panel) return;
      const focusables = panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
      previouslyFocused.current?.focus();
    };
  }, [open]);

  // Görsel viewport → `--modal-vh` / `--modal-top`. Klavye açılınca hizalama
  // kabı hem kısalır hem klavyenin üstüne kayar; pencere görünür alanda kalır.
  useEffect(() => {
    if (!open) return;
    const vv = window.visualViewport;
    const box = viewportRef.current;
    if (!vv || !box) return;

    const sync = () => {
      box.style.setProperty("--modal-vh", `${Math.round(vv.height)}px`);
      box.style.setProperty("--modal-top", `${Math.round(vv.offsetTop)}px`);
    };
    sync();
    vv.addEventListener("resize", sync);
    vv.addEventListener("scroll", sync);
    return () => {
      vv.removeEventListener("resize", sync);
      vv.removeEventListener("scroll", sync);
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="noprint fixed inset-0 z-50">
      <div
        className="fixed inset-0 bg-gray-900/60"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Hizalama kabı = görsel viewport. Panel bunu asla geçemez. */}
      <div
        ref={viewportRef}
        className="modal-viewport absolute inset-x-0 flex items-end justify-center sm:items-center sm:p-4"
      >
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={description ? descId : undefined}
          tabIndex={-1}
          className={`relative flex max-h-full w-full ${WIDTH[size]} flex-col overflow-hidden rounded-t-xl bg-white shadow-xl sm:rounded-xl`}
        >
          {/* Başlık — kaymaz, her zaman görünür. */}
          <div className="flex shrink-0 items-start justify-between gap-3 px-4 pt-4 sm:px-6 sm:pt-6">
            <div className="min-w-0">
              <h2 id={titleId} className="text-base font-semibold text-gray-900">
                {title}
              </h2>
              {description ? (
                <p id={descId} className="mt-1 text-sm text-gray-600">
                  {description}
                </p>
              ) : null}
            </div>
            <button
              type="button"
              className="-mr-2 -mt-2 inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 hover:text-gray-900"
              onClick={onClose}
              aria-label="Pencereyi kapat"
            >
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
              </svg>
            </button>
          </div>

          {/* Gövde — uzun içerik SAYFAYI değil BU KABI kaydırır. */}
          {children ? (
            <div className="modal-body px-4 pb-4 pt-4 sm:px-6 sm:pb-6">{children}</div>
          ) : (
            <div className="pb-4" />
          )}

          {/* Alt aksiyon çubuğu — kaymaz, her zaman görünür. */}
          {footer ? (
            <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-gray-200 bg-white px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-6 sm:py-4">
              {footer}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default Modal;
